import React, { useState, useEffect } from 'react';
import { 
  FilterIcon, 
  SearchIcon, 
  PackageIcon, 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  XIcon,
  ChevronDownIcon 
} from 'lucide-react';
import { subscriptionPlanServices, SubscriptionPlan } from '../lib/supabase';

const Plans = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [currentPlan, setCurrentPlan] = useState<Partial<SubscriptionPlan> | null>(null);

  // Fetch subscription plans on component mount
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true);
        
        const { data, error } = await subscriptionPlanServices.getAll();
        
        if (error) {
          throw error;
        }
        
        if (data) {
          setPlans(data);
        }
      } catch (error: any) {
        setError(error.message || 'Failed to fetch subscription plans');
        console.error('Error fetching plans:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  // Filter plans by search query
  const filteredPlans = plans.filter(plan =>
    plan.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    plan.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Open modal to add/edit plan
  const handleOpenModal = (plan: SubscriptionPlan | null = null) => {
    setCurrentPlan(plan || {
      name: '',
      description: '',
      price: 0,
      billing_period: 'monthly',
      features: {},
      active: true,
    });
    setShowModal(true);
  };
  
  // Close modal and reset form
  const handleCloseModal = () => {
    setShowModal(false);
    setCurrentPlan(null);
  };
  
  // Handle plan form submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentPlan || !currentPlan.name) {
      setError('Please fill all required fields');
      return;
    }
    
    try {
      let result: { data: SubscriptionPlan[] | null; error: Error | null };
      
      if (currentPlan.id) {
        // Update existing plan
        result = await subscriptionPlanServices.update(currentPlan.id, currentPlan);
        
        // Update local state
        setPlans(prevPlans => 
          prevPlans.map(plan => 
            plan.id === currentPlan.id 
              ? { ...plan, ...currentPlan } 
              : plan
          )
        );
      } else {
        // Create new plan
        result = await subscriptionPlanServices.create(currentPlan as SubscriptionPlan);
        
        // Update local state
        if (result.data) {
          setPlans(prevPlans => [...prevPlans, result.data![0]]);
        }
      }
      
      if (result.error) {
        throw result.error;
      }
      
      handleCloseModal();
    } catch (error: any) {
      setError(error.message || 'Failed to save subscription plan');
      console.error('Error saving plan:', error);
    }
  };
  
  // Handle plan deletion
  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this subscription plan?')) {
      try {
        const { error } = await subscriptionPlanServices.delete(id);
        
        if (error) {
          throw error;
        }
        
        // Update local state
        setPlans(prevPlans => prevPlans.filter(plan => plan.id !== id));
      } catch (error: any) {
        setError(error.message || 'Failed to delete subscription plan');
        console.error('Error deleting plan:', error);
      }
    }
  };

  // Render loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-800">Subscription Plans</h1>
        
        <div className="flex flex-wrap gap-2">
          <div className="relative">
            <input
              type="text"
              placeholder="Search plans..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm w-60"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <SearchIcon size={16} className="text-gray-500" />
            </div>
          </div>
          
          <button 
            onClick={() => handleOpenModal()}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-sm font-medium"
          >
            <PlusIcon size={16} className="mr-2" />
            New Plan
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md flex items-start">
          <div className="flex-shrink-0 mt-0.5">
            <XIcon size={16} />
          </div>
          <div className="ml-3">
            <p className="text-sm font-medium">{error}</p>
          </div>
          <button 
            className="ml-auto flex-shrink-0 text-red-500 hover:text-red-600"
            onClick={() => setError(null)}
          >
            <XIcon size={16} />
          </button>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Billing Period
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredPlans.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500">
                    No subscription plans found
                  </td>
                </tr>
              ) : (
                filteredPlans.map((plan) => (
                  <tr key={plan.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {plan.name}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                      {plan.description || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${plan.price.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-capitalize text-gray-900">
                      {plan.billing_period === 'monthly' ? 'Monthly' : 'Annual'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span 
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          plan.active 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {plan.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-3">
                        <button 
                          onClick={() => handleOpenModal(plan)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <PencilIcon size={16} />
                          <span className="sr-only">Edit</span>
                        </button>
                        
                        <button 
                          onClick={() => plan.id && handleDelete(plan.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <TrashIcon size={16} />
                          <span className="sr-only">Delete</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Plan Modal */}
      {showModal && (
        <div className="fixed inset-0 overflow-y-auto z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"></div>
          
          <div className="relative bg-white rounded-lg max-w-2xl w-full mx-4 shadow-xl transform transition-all">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">
                  {currentPlan?.id ? 'Edit Plan' : 'New Plan'}
                </h3>
                <button 
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XIcon size={20} />
                </button>
              </div>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="px-6 py-4 space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    placeholder="Plan name"
                    value={currentPlan?.name || ''}
                    onChange={(e) => setCurrentPlan({...currentPlan, name: e.target.value})}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                
                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    id="description"
                    placeholder="Plan description"
                    value={currentPlan?.description || ''}
                    onChange={(e) => setCurrentPlan({...currentPlan, description: e.target.value})}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-1">
                      Price *
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 sm:text-sm">$</span>
                      </div>
                      <input
                        type="number"
                        id="price"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={currentPlan?.price || ''}
                        onChange={(e) => setCurrentPlan({...currentPlan, price: Number(e.target.value)})}
                        required
                        className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="billing_period" className="block text-sm font-medium text-gray-700 mb-1">
                      Billing Period *
                    </label>
                    <select
                      id="billing_period"
                      value={currentPlan?.billing_period || 'monthly'}
                      onChange={(e) => setCurrentPlan({...currentPlan, billing_period: e.target.value as 'monthly' | 'annual'})}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="monthly">Monthly</option>
                      <option value="annual">Annual</option>
                    </select>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="active"
                    checked={currentPlan?.active !== false}
                    onChange={(e) => setCurrentPlan({...currentPlan, active: e.target.checked})}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="active" className="ml-2 text-sm text-gray-700">
                    Active
                  </label>
                </div>
              </div>
              
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {currentPlan?.id ? 'Update Plan' : 'Create Plan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Plans;
